import React, { useMemo } from 'react'
import moment from 'moment-business-days'
import styles from './styles.module.scss'
import { setMonthlyWorkingHours } from 'components/utils/WorkFunctions'
import LoadingSpinner from 'components/public/landing/loadingSpinner/LoadingSpinner'
import { Absence } from 'Types/Absences/Absence'
import { Filter } from 'Types/Filter'
import { IHoliday } from 'Types/Holidays/IHoliday'
import { ITimetrackInterface } from 'Types/TimeTrack/ITimeTrack'
import {
  DATE_FORMAT,
  expandAbsenceDates,
  getMonthRange,
  toHolidayDates,
} from '../utils/absenceDays'

const classNames = (...names: Array<string | false>) => names.filter(Boolean).join(' ')

interface IProps {
  timetracks: Array<ITimetrackInterface[]>
  holidays: IHoliday[] | undefined
  filters: Filter[]
  absences: Absence[]
  loading: boolean
}

const WorkingDays = ({ timetracks, holidays, filters, absences, loading }: IProps) => {
  const { monthStart, monthEnd } = useMemo(() => getMonthRange(filters), [filters])

  // Business days the user is expected to work: no holidays, no time off
  const requiredDays = useMemo(() => {
    const holidayDates = toHolidayDates(holidays)
    const absenceDates = expandAbsenceDates(absences, monthStart, monthEnd)

    return moment(monthStart)
      .monthBusinessDays()
      .filter((date) => {
        const formatted = date.format(DATE_FORMAT)
        return !holidayDates.has(formatted) && !absenceDates.has(formatted)
      })
  }, [holidays, absences, monthStart, monthEnd])

  const unfilledDays = useMemo(() => {
    const trackedDates = new Set(setMonthlyWorkingHours(timetracks).dates)
    return requiredDays.filter((date) => !trackedDates.has(date.format(DATE_FORMAT)))
  }, [requiredDays, timetracks])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.spinnerContainer}>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  const filledCount = requiredDays.length - unfilledDays.length
  const progress = requiredDays.length ? (filledCount / requiredDays.length) * 100 : 100
  const allFilled = unfilledDays.length === 0

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>Unfilled days</span>
        <span className={classNames(styles.badge, allFilled ? styles.success : styles.danger)}>
          {unfilledDays.length} {unfilledDays.length === 1 ? 'day' : 'days'}
        </span>
      </div>

      <div className={styles.progress}>
        <div className={styles.progressTrack}>
          <div
            className={classNames(styles.progressBar, allFilled && styles.progressBarDone)}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className={styles.progressLabel}>
          {filledCount} / {requiredDays.length} days filled
        </span>
      </div>

      {allFilled ? (
        <span className={styles.allFilled}>All working days filled ✓</span>
      ) : (
        <div className={styles.dates}>
          {unfilledDays.map((date) => (
            <span
              key={date.format(DATE_FORMAT)}
              className={classNames(
                styles.chip,
                date.isSameOrBefore(moment(), 'day') ? styles.chipOverdue : styles.chipUpcoming
              )}
              title={date.isSameOrBefore(moment(), 'day') ? 'Not filled' : 'Upcoming'}
            >
              {date.format('ddd DD.MM.')}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default WorkingDays
